import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Chip,
  Stack,
} from '@mui/material';
import {
  BackButton, DebouncedSearchInput, OutlinedIcons, TableAccordionInfinite, TableAccordionColumn,
} from '@marageti/z4-lib';
import { EntityType, EntityVersion, AnyEntityVersion } from '../types';
import { computeEntityVersionDiff } from './use-version-diff';
import EntitySelector from './entity-selector';

const { OutlinedInformationCircleIcon } = OutlinedIcons;

const buildColumns = (
  diffs: Record<string, string[]>,
  firstUpdateId: string | undefined,
): TableAccordionColumn<EntityVersion>[] => [
  {
    fieldPath: 'updateId',
    label: 'Update ID',
    style: { minWidth: 150 },
  },
  {
    fieldPath: 'updatedAt',
    label: 'Updated At',
    style: { minWidth: 180 },
    format: (value) => (value ? new Date(String(value)).toLocaleString() : '-'),
  },
  {
    fieldPath: 'status',
    label: 'Status',
    style: { minWidth: 100 },
    format: (value) => String(value || '-'),
  },
  {
    fieldPath: 'changes',
    label: 'Changes',
    style: { minWidth: 200 },
    format: (_value, row) => {
      const isInitial = row.updateId === firstUpdateId;
      const changes = diffs[row.updateId];

      let chips: React.ReactNode;
      if (isInitial) {
        chips = (
          <Typography variant="caption" color="text.secondary">
            (Initial)
          </Typography>
        );
      } else if (!changes) {
        chips = (
          <Typography variant="caption" color="text.secondary">
            ...
          </Typography>
        );
      } else if (changes.length === 0) {
        chips = (
          <Typography variant="caption" color="text.secondary">
            No changes
          </Typography>
        );
      } else {
        chips = changes.slice(0, 3).map((key) => (
          <Chip key={key} label={key} size="small" variant="outlined" />
        ));
      }

      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {chips}
          {changes && changes.length > 3 && (
            <Chip label={`+${changes.length - 3}`} size="small" variant="outlined" />
          )}
        </Box>
      );
    },
  },
];

interface VersionsViewProps {
  versions: EntityVersion[];
  rawVersions: AnyEntityVersion[];
  isLoading: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => void;
  filters: {
    entityType: EntityType | '';
    id: string;
    all: boolean;
  };
  onFilterChange: (name: string, value: unknown) => void;
  onVersionClick: (version: EntityVersion) => void;
}

const VersionsView: React.FC<VersionsViewProps> = ({
  versions,
  rawVersions,
  isLoading,
  error,
  hasMore,
  loadMore,
  filters,
  onFilterChange,
  onVersionClick,
}) => {
  const navigate = useNavigate();
  const [diffs, setDiffs] = useState<Record<string, string[]>>({});
  const computedIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    computedIds.current.clear();
    setDiffs({});
  }, [rawVersions]);

  useEffect(() => {
    if (rawVersions.length < 2) return;

    const timer = setTimeout(() => {
      const newDiffs: Record<string, string[]> = {};
      let hasNewDiffs = false;

      for (let i = 1; i < rawVersions.length; i += 1) {
        const current = rawVersions[i];
        const previous = rawVersions[i - 1];

        if (computedIds.current.has(current.updateId)) {
          // eslint-disable-next-line no-continue
          continue;
        }

        const changes = computeEntityVersionDiff(current, previous);
        const changedKeys = changes
          .filter((c) => c.hasChanges)
          .map((c) => c.key);

        newDiffs[current.updateId] = changedKeys;
        computedIds.current.add(current.updateId);
        hasNewDiffs = true;
      }

      if (hasNewDiffs) {
        setDiffs((prev) => ({ ...prev, ...newDiffs }));
      }
    }, 100);

    // eslint-disable-next-line consistent-return
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [rawVersions]);

  const columns = useMemo(
    () => buildColumns(diffs, versions[0]?.updateId),
    [diffs, versions],
  );

  return (
    <Box>
      <Stack alignItems="center" justifyContent="flex-start" flexDirection="row" gap={1} sx={{ mb: 2 }}>
        <BackButton onClick={() => navigate('/settings/audit')} />
        <Typography variant="h5">
          Entity Versions
        </Typography>
      </Stack>

      <Box sx={{
        display: 'flex',
        gap: 2,
        flexWrap: 'wrap',
        alignItems: 'center',
      }}
      >
        <EntitySelector
          value={filters.entityType}
          onChange={(value) => onFilterChange('entityType', value)}
          size="small"
          sx={{
            minWidth: 150,
            marginTop: '20px',
            marginBottom: '10px',
          }}
        />
        <DebouncedSearchInput
          label="Entity ID"
          defaultValue={filters.id}
          onChange={(value: string) => onFilterChange('id', value)}
          size="small"
          sx={{
            minWidth: 200,
            maxWidth: 200,
            marginTop: '20px',
            marginBottom: '10px',
          }}
        />
        <FormControlLabel
          control={(
            <Checkbox
              checked={filters.all}
              onChange={(e) => onFilterChange('all', e.target.checked)}
            />
          )}
          label={(
            <Box display="flex" alignItems="center" gap={0.5}>
              Show all versions
              <Tooltip title="If enabled, shows all versions including internal updates. If disabled, shows only major versions.">
                <Box display="flex" alignItems="center">
                  <OutlinedInformationCircleIcon fontSize="small" color="action" />
                </Box>
              </Tooltip>
            </Box>
          )}
          sx={{
            marginTop: '10px',
          }}
        />
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          Error:
          {' '}
          {error?.message || 'An error occurred while retrieving versions'}
        </Typography>
      )}

      <Typography variant="caption" component="p" sx={{ mt: 4 }}>
        {`${versions.length} versions loaded`}
      </Typography>

      <TableAccordionInfinite<EntityVersion>
        columns={columns}
        data={versions}
        getRowId={(v) => v.updateId}
        isLoading={isLoading}
        hasMore={hasMore}
        loadMore={loadMore}
        emptyMessage="No versions found. Select an entity type and enter an ID."
        isEmpty={versions.length === 0}
        onRowClick={onVersionClick}
        rowSx={() => ({ cursor: 'pointer' })}
      />
    </Box>
  );
};

export default VersionsView;
